import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend } from "recharts";
import { Check, X, ShieldCheck, ListX, CalendarClock } from "lucide-react";
import { Link } from "react-router-dom";
import { DOMAIN_SHORT } from "@/lib/api";
import { PASS } from "@/components/GradeChart";

const axis = { fontSize: 11, fill: "var(--ink-3)" };
const tipStyle = { background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 8, fontSize: 12 };
const H = ({ icon: Icon, children }) => <h3 className="font-display font-semibold text-sm flex items-center gap-2 mb-3"><Icon size={15} style={{ color: "var(--accent)" }} /> {children}</h3>;

export const SafetyCard = ({ vivas }) => {
  const rows = vivas.filter((v) => v.examiners?.length).map((v, i) => {
    const safe = v.examiners.filter((e) => e.safe).length;
    return { n: i + 1, title: v.case_title, both: safe === 2 ? 1 : 0, one: safe === 1 ? 1 : 0, none: safe === 0 ? 1 : 0 };
  });
  if (!rows.length) return <div data-testid="perf-safety" className="card p-5"><H icon={ShieldCheck}>Safety verdicts</H><p className="text-sm" style={{ color: "var(--ink-3)" }}>Examiner safety verdicts appear for cases marked from now on.</p></div>;
  const both = rows.filter((r) => r.both).length;
  const recent = rows.slice(-5);
  return (
    <div data-testid="perf-safety" className="card p-5">
      <H icon={ShieldCheck}>Safety verdicts: both examiners "safe"</H>
      <p className="text-sm"><span data-testid="perf-safety-rate" className="font-mono text-2xl font-bold" style={{ color: both / rows.length >= 13 / 16 ? "var(--ok)" : "var(--alert)" }}>{Math.round((both / rows.length) * 100)}%</span>
        <span style={{ color: "var(--ink-3)" }}> of {rows.length} cases · last 5: {recent.filter((r) => r.both).length}/{recent.length}</span></p>
      <div style={{ height: 150 }} className="mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <XAxis dataKey="n" tick={axis} tickLine={false} axisLine={false} />
            <YAxis hide domain={[0, 1]} />
            <Tooltip cursor={{ fill: "var(--muted-bg)" }} contentStyle={tipStyle} labelFormatter={(n) => rows[n - 1]?.title} formatter={(v, k) => (v ? [k, ""] : [null, null])} />
            <Bar dataKey="both" name="Both safe" stackId="s" fill="var(--ok)" />
            <Bar dataKey="one" name="Split (1 of 2)" stackId="s" fill="var(--warn)" />
            <Bar dataKey="none" name="Neither safe" stackId="s" fill="var(--alert)" />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-1 text-xs" style={{ color: "var(--ink-3)" }}>The exam needs at least 13 of 16 examiners (81%) to judge you safe. Indicative only.</p>
    </div>
  );
};

const norm = (s) => s.toLowerCase().replace(/[^a-z0-9%₂ ]+/g, " ").replace(/\s+/g, " ").trim();

export const MissedBasics = ({ vivas, driving }) => {
  const counts = new Map();
  const add = (s) => { const k = norm(s); if (!k) return; const c = counts.get(k) || { label: s.trim(), n: 0 }; c.n += 1; counts.set(k, c); };
  vivas.forEach((v) => v.questions?.forEach((q) => q.basics_missed?.forEach(add)));
  driving.forEach((d) => d.missed_must_say?.forEach(add));
  const top = [...counts.values()].sort((a, b) => b.n - a.n).slice(0, 8);
  const max = top[0]?.n || 1;
  return (
    <div data-testid="perf-missed-basics" className="card p-5">
      <H icon={ListX}>Most-missed basics</H>
      {top.length ? (
        <ul className="space-y-2">
          {top.map((t, i) => (
            <li key={t.label} data-testid={`perf-missed-${i}`} className="grid grid-cols-[1fr,auto] gap-3 items-center text-sm">
              <span><span className="block">{t.label}</span><span className="block h-1.5 mt-1 rounded-full" style={{ width: `${(t.n / max) * 100}%`, background: "var(--warn)" }} /></span>
              <span className="font-mono text-xs font-bold">×{t.n}</span>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm" style={{ color: "var(--ink-3)" }}>No missed basics recorded yet.</p>}
      <p className="mt-3 text-xs" style={{ color: "var(--ink-3)" }}>From case feedback ("say it out loud" basics) and Driving Mode must-say points.</p>
    </div>
  );
};

export const RubricTrend = ({ vivas }) => {
  const rows = vivas.filter((v) => v.rubric).map((v, i) => ({ n: i + 1, qs: v.rubric.quality_safety, rc: v.rubric.rural_context, st: v.rubric.structure }));
  if (rows.length < 2) return null;
  return (
    <div data-testid="perf-rubric-trend" style={{ height: 170 }} className="mt-4">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 4, right: 8, left: -30, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
          <XAxis dataKey="n" tick={axis} tickLine={false} axisLine={false} />
          <YAxis domain={[0, 4]} ticks={[0, 1, 2, 3, 4]} tick={axis} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={tipStyle} labelFormatter={(n) => `Case ${n}`} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="qs" name="Quality & safety" stroke="var(--accent)" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="rc" name="Rural context" stroke="var(--warn)" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="st" name="Structure" stroke="var(--ok)" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const MockHistory = ({ mocks }) => (
  <div data-testid="perf-mocks" className="card p-5">
    <H icon={CalendarClock}>Mock Exam Day results</H>
    {mocks.length ? (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-xs text-left" style={{ color: "var(--ink-3)" }}><th className="font-medium pb-2">Date</th><th className="font-medium pb-2">Stations</th><th className="font-medium pb-2">Score 50%+</th><th className="font-medium pb-2">Vivas passed</th><th className="font-medium pb-2">Examiners safe</th><th className="font-medium pb-2">Result</th></tr></thead>
          <tbody>
            {[...mocks].reverse().map((m, i) => (
              <tr key={m.mock_id} data-testid={`perf-mock-row-${i}`} className="border-t" style={{ borderColor: "var(--line)" }}>
                <td className="py-2 font-mono text-xs">{new Date(m.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</td>
                <td className="py-2">{m.stations.length}</td>
                {["score", "stations", "safety"].map((k) => { const r = m.rules.find((x) => x.key === k); return (
                  <td key={k} className="py-2"><span className="inline-flex items-center gap-1 font-mono text-xs whitespace-nowrap" style={{ color: r?.ok ? "var(--ok)" : "var(--alert)" }}>{r?.ok ? <Check size={13} /> : <X size={13} />}{r?.value}</span></td>); })}
                <td className="py-2"><span className="px-2 py-0.5 rounded-full text-xs font-semibold text-white" style={{ background: m.passed ? "var(--ok)" : "var(--alert)" }}>{m.passed ? "Pass" : "Fail"}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : <p className="text-sm" style={{ color: "var(--ink-3)" }}>No mock exam days completed yet. <Link to="/mock" className="underline">Sit one</Link></p>}
  </div>
);

export const Readiness = ({ examDate, domains }) => {
  const below = domains.filter((d) => d.viva != null && d.viva < PASS);
  const untried = domains.filter((d) => d.viva == null);
  const days = examDate ? Math.ceil((new Date(`${examDate}T00:00:00`) - new Date().setHours(0, 0, 0, 0)) / 864e5) : null;
  const todo = below.length + untried.length;
  const perWeek = days > 0 ? Math.max(1, Math.ceil(todo / Math.max(1, Math.floor(days / 7)))) : 0;
  return (
    <div data-testid="perf-readiness" className="card p-5">
      <H icon={CalendarClock}>Exam readiness</H>
      <div className="grid grid-cols-2 gap-4">
        <div><p className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--ink-3)" }}>Days to exam</p>
          <p data-testid="perf-readiness-days" className="font-display text-3xl font-bold">{days == null ? "–" : Math.max(0, days)}</p></div>
        <div><p className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--ink-3)" }}>Domains below pass</p>
          <p data-testid="perf-readiness-below" className="font-display text-3xl font-bold" style={{ color: below.length ? "var(--alert)" : untried.length === domains.length ? "var(--ink)" : "var(--ok)" }}>{below.length}<span className="text-base font-normal" style={{ color: "var(--ink-3)" }}> + {untried.length} with no cases yet</span></p></div>
      </div>
      {below.length > 0 && <p className="mt-3 text-sm">Below the line: {below.map((d) => `D${d.n} ${DOMAIN_SHORT[d.n - 1]} (${d.viva}%)`).join(", ")}</p>}
      <p data-testid="perf-readiness-advice" className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
        {days == null ? <>Set your exam date on the <Link to="/" className="underline">home page</Link> to see your countdown.</>
          : days <= 0 ? "Exam day has arrived. Good luck!"
          : todo === 0 ? "Every domain is at or above the pass line. Keep it ticking over with mock days."
          : perWeek > 3 ? `With ${days} day${days > 1 ? "s" : ""} to go, prioritise ${below.length ? "the domains below the line" : "a few untried domains"} and a mock exam day rather than trying to cover everything.`
          : `About ${perWeek} domain${perWeek > 1 ? "s" : ""} to lift per week to cover them all before the exam.`}
      </p>
      <p className="mt-2 text-xs" style={{ color: "var(--ink-3)" }}>Indicative only.</p>
    </div>
  );
};
